<template>
  <div>
    <AppInputLabel v-if="label" :name="name" :label="label" :hasError="hasError(name).value" :required="required" />
    <div class="relative">
      <div class="absolute inset-y-0 left-3 flex items-center">
        <slot name="prefix"></slot>
      </div>
      <input :id="name" class="w-full rounded-lg px-3 py-2" :class="[
        { 'border-critial': hasError(name).value },
        { 'pl-10': $slots.prefix }
      ]" :disabled="disabled" :checked="innerValue === true" :indeterminate="innerValue === null" type="checkbox"
        @input="handleInput" @change="handleInput" @blur="validateInput" ref="checkboxRef" />
    </div>
    <AppInputError v-show="hasError(name)" :error="errors.get(name)" />
  </div>
</template>

<script setup lang="ts">
import { inject, ref, watch, onBeforeUnmount, onMounted } from "vue";
import { ValidationContextKey } from "@/models/Context";
import useFormValidation from "@/modules/useFormValidation";
import AppInputLabel from "./AppInputLabel.vue";
import AppInputError from "./AppInputError.vue";

const props = defineProps<{
  name: string;
  label?: string;
  modelValue?: boolean | null;
  disabled?: boolean;
  required?: boolean;
}>();

const emit = defineEmits(["update:modelValue"]);

const innerValue = ref(props.modelValue);
const checkboxRef = ref<HTMLInputElement | null>(null);


// Create a default validation context if none is provided
const defaultContext = {
  errors: new Map<string, string>(),
  validators: {} as Record<string, (key: string, validator: string) => void>,
  registerValidator: (key: string, validator: (key: string, validator: string) => void) => {
    defaultContext.validators[key] = validator;
  }
};

const context = inject(ValidationContextKey, defaultContext);
const { validateRequiredField, clearValidator, clearError, errors, registerValidator, hasError } = useFormValidation(context);

const validateInput = () => {
  if (!props.required) return;

  clearError(props.name);
  validateRequiredField(props.name, props.label || props.name, innerValue.value as string);
};

const handleInput = (event: InputEvent) => {
  // Emit immediately, no timeout needed
  emit("update:modelValue", event.target?.checked ?? false);
  validateInput();
};

watch(
  () => props.modelValue,
  (newVal) => {
    innerValue.value = newVal;
    console.log("appinput", props.label, innerValue.value);
    validateInput();
  }
);

watch(innerValue, () => {
  if (checkboxRef.value) {
    checkboxRef.value.indeterminate = innerValue.value === null;
  }
}, { immediate: true });


onBeforeUnmount(() => {
  if (props.required) {
    clearValidator(props.name);
    clearError(props.name);
  }
});

onMounted(() => {
  if (checkboxRef.value) {
    checkboxRef.value.indeterminate = innerValue.value === null;
  }
});

registerValidator(props.name, validateInput);
</script>